import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth, useUserMap } from "@/context/AuthContext";
import { fmtDate, fmtDateLong, todayStr } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { SHOOT_STATUS, SHOOT_STATUS_ORDER, SHOOT_TYPES, EQUIPMENT, TEAM_ROLES, label } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Loader2, Camera, MapPin, Clock, Users2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const emptyShoot = (client_id) => ({
  client_id: client_id || "", shoot_type: "Product Shoot", date: todayStr(), start_time: "10:00", end_time: "14:00",
  location: "", contact_person: "", purpose: "", brief: "", notes: "", status: "scheduled",
  team: [], equipment: [],
});

function ShootFormDialog({ open, onOpenChange, initial, defaultClient, onSaved }) {
  const { users } = useAuth();
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const [f, setF] = useState(initial || emptyShoot(defaultClient));
  const [saving, setSaving] = useState(false);
  React.useEffect(() => { setF(initial || emptyShoot(defaultClient)); }, [initial, open, defaultClient]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const addMember = () => set("team", [...f.team, { user_id: users[0]?.id || "", role: "photographer" }]);
  const setMember = (i, k, v) => set("team", f.team.map((m, j) => (j === i ? { ...m, [k]: v } : m)));
  const rmMember = (i) => set("team", f.team.filter((_, j) => j !== i));
  const toggleEq = (e) => set("equipment", f.equipment.includes(e) ? f.equipment.filter((x) => x !== e) : [...f.equipment, e]);

  const save = async () => {
    if (!f.client_id) return toast.error("Select a client");
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/shoots/${initial.id}`, f);
      else await api.post("/shoots", f);
      toast.success(initial?.id ? "Shoot updated" : "Shoot scheduled");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Edit Shoot" : "Schedule Shoot"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-3 py-2 sm:grid-cols-2">
          <div>
            <Label className="text-xs text-zinc-500">Client *</Label>
            <Select value={f.client_id} onValueChange={(v) => set("client_id", v)} disabled={!!defaultClient}>
              <SelectTrigger className="mt-1" data-testid="shoot-client-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Shoot Type</Label>
            <Select value={f.shoot_type} onValueChange={(v) => set("shoot_type", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{SHOOT_TYPES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-zinc-500">Date</Label><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-zinc-500">Start</Label><Input type="time" value={f.start_time} onChange={(e) => set("start_time", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs text-zinc-500">End</Label><Input type="time" value={f.end_time} onChange={(e) => set("end_time", e.target.value)} className="mt-1" /></div>
          </div>
          <div className="sm:col-span-2"><Label className="text-xs text-zinc-500">Location</Label><Input value={f.location} onChange={(e) => set("location", e.target.value)} className="mt-1" /></div>
          <div><Label className="text-xs text-zinc-500">Contact Person</Label><Input value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} className="mt-1" /></div>
          <div><Label className="text-xs text-zinc-500">Purpose</Label><Input value={f.purpose} onChange={(e) => set("purpose", e.target.value)} className="mt-1" /></div>
          <div className="sm:col-span-2"><Label className="text-xs text-zinc-500">Brief</Label><Textarea value={f.brief} onChange={(e) => set("brief", e.target.value)} className="mt-1" rows={2} /></div>

          <div className="sm:col-span-2">
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-xs text-zinc-500">Team</Label>
              <Button size="sm" variant="outline" onClick={addMember} type="button"><Plus className="mr-1 h-3 w-3" />Add</Button>
            </div>
            <div className="space-y-2">
              {f.team.map((m, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Select value={m.user_id} onValueChange={(v) => setMember(i, "user_id", v)}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Member" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={m.role} onValueChange={(v) => setMember(i, "role", v)}>
                    <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>{TEAM_ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <button onClick={() => rmMember(i)} className="p-2 text-zinc-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <Label className="text-xs text-zinc-500">Equipment</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {EQUIPMENT.map((e) => (
                <button key={e} type="button" onClick={() => toggleEq(e)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${f.equipment.includes(e) ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>{e}</button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="shoot-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShootDetailDialog({ shoot, open, onOpenChange, onSaved }) {
  const umap = useUserMap();
  const [status, setStatus] = useState(shoot?.status);
  const [outcome, setOutcome] = useState(shoot?.outcome || {});
  const [deliv, setDeliv] = useState(shoot?.deliverables || {});
  const [saving, setSaving] = useState(false);
  React.useEffect(() => { setStatus(shoot?.status); setOutcome(shoot?.outcome || {}); setDeliv(shoot?.deliverables || {}); }, [shoot]);
  if (!shoot) return null;

  const setO = (k, v) => setOutcome((p) => ({ ...p, [k]: v }));
  const setD = (k, v) => setDeliv((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/shoots/${shoot.id}`, { status, outcome: { ...outcome, completed: status === "completed" ? true : outcome.completed }, deliverables: deliv });
      toast.success("Shoot updated");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const Check = ({ k, l, state, setter }) => (
    <label className="flex items-center gap-2 text-sm"><Switch checked={!!state[k]} onCheckedChange={(v) => setter(k, v)} />{l}</label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">{shoot.shoot_type} <span className="text-sm font-normal text-zinc-400">{shoot.code}</span></DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-zinc-600"><Clock className="h-4 w-4 text-zinc-400" />{fmtDateLong(shoot.date)} · {shoot.start_time}–{shoot.end_time}</div>
            <div className="flex items-center gap-2 text-zinc-600"><MapPin className="h-4 w-4 text-zinc-400" />{shoot.location || "—"}</div>
          </div>
          <div>
            <div className="mb-1.5 flex items-center gap-2 text-zinc-600"><Users2 className="h-4 w-4 text-zinc-400" />Team</div>
            <div className="flex flex-wrap gap-2">
              {(shoot.team || []).map((m, i) => <Badge key={i} color="blue">{umap[m.user_id]?.name || "?"} · {m.role.replace("_", " ")}</Badge>)}
              {(shoot.team || []).length === 0 && <span className="text-zinc-400">No team assigned</span>}
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1.5" data-testid="shoot-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SHOOT_STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{SHOOT_STATUS[s].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="rounded-lg border border-zinc-100 p-4">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Shoot Outcome</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><Label className="text-xs text-zinc-500">Actual Start</Label><Input value={outcome.actual_start || ""} onChange={(e) => setO("actual_start", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs text-zinc-500">Actual End</Label><Input value={outcome.actual_end || ""} onChange={(e) => setO("actual_end", e.target.value)} className="mt-1" /></div>
              <div className="sm:col-span-2"><Label className="text-xs text-zinc-500">What was captured?</Label><Textarea value={outcome.captured || ""} onChange={(e) => setO("captured", e.target.value)} className="mt-1" rows={2} /></div>
              <div className="sm:col-span-2"><Label className="text-xs text-zinc-500">Issues</Label><Input value={outcome.issues || ""} onChange={(e) => setO("issues", e.target.value)} className="mt-1" /></div>
              <div className="sm:col-span-2"><Label className="text-xs text-zinc-500">Client Feedback</Label><Input value={outcome.client_feedback || ""} onChange={(e) => setO("client_feedback", e.target.value)} className="mt-1" /></div>
              <div className="flex items-center gap-2 pt-2"><Switch checked={!!outcome.completed} onCheckedChange={(v) => setO("completed", v)} /><span>Shoot completed & documented</span></div>
            </div>
          </div>

          <div className="rounded-lg border border-zinc-100 p-4">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Deliverables</div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <div className="text-sm font-medium">Photos</div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs text-zinc-500">Expected</Label><Input type="number" value={deliv.photos_expected || 0} onChange={(e) => setD("photos_expected", Number(e.target.value))} className="mt-1" /></div>
                  <div><Label className="text-xs text-zinc-500">Actual</Label><Input type="number" value={deliv.photos_actual || 0} onChange={(e) => setD("photos_actual", Number(e.target.value))} className="mt-1" /></div>
                </div>
                <Check k="photos_uploaded" l="Uploaded" state={deliv} setter={setD} />
                <Check k="photos_reviewed" l="Reviewed" state={deliv} setter={setD} />
                <Check k="photos_delivered" l="Delivered" state={deliv} setter={setD} />
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">Videos</div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs text-zinc-500">Expected</Label><Input type="number" value={deliv.videos_expected || 0} onChange={(e) => setD("videos_expected", Number(e.target.value))} className="mt-1" /></div>
                  <div><Label className="text-xs text-zinc-500">Actual</Label><Input type="number" value={deliv.videos_actual || 0} onChange={(e) => setD("videos_actual", Number(e.target.value))} className="mt-1" /></div>
                </div>
                <Check k="videos_edited" l="Edited" state={deliv} setter={setD} />
                <Check k="videos_reviewed" l="Reviewed" state={deliv} setter={setD} />
                <Check k="videos_delivered" l="Delivered" state={deliv} setter={setD} />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 border-t border-zinc-100 pt-3 sm:grid-cols-2">
              <div><Label className="text-xs text-zinc-500">Raw Storage Location</Label><Input value={deliv.raw_location || ""} onChange={(e) => setD("raw_location", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs text-zinc-500">Delivery Date</Label><Input type="date" value={deliv.delivery_date || ""} onChange={(e) => setD("delivery_date", e.target.value)} className="mt-1" /></div>
              <Check k="raw_uploaded" l="Raw files uploaded" state={deliv} setter={setD} />
              <Check k="final_delivered" l="Final files delivered to client" state={deliv} setter={setD} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="shoot-outcome-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShootCard({ s, clientName, onClick }) {
  return (
    <Card onClick={onClick} data-testid={`shoot-card-${s.id}`} className="cursor-pointer rounded-lg border-zinc-200 p-4 shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-50">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-zinc-100 text-zinc-500"><Camera className="h-4 w-4" /></div>
          <div>
            <div className="font-medium text-zinc-900">{s.shoot_type}</div>
            <div className="text-xs text-zinc-500">{clientName} · {s.code}</div>
          </div>
        </div>
        <Badge color={SHOOT_STATUS[s.status]?.color} dot>{label(SHOOT_STATUS, s.status)}</Badge>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(s.date)} · {s.start_time}</span>
        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{s.location || "—"}</span>
        <span className="flex items-center gap-1"><Users2 className="h-3 w-3" />{(s.team || []).length} team</span>
      </div>
      {s.status === "completed" && (
        <div className="mt-3 flex gap-2">
          {!(s.outcome || {}).completed && <Badge color="orange">Outcome pending</Badge>}
          {(s.outcome || {}).completed && !(s.deliverables || {}).final_delivered && <Badge color="orange">Delivery pending</Badge>}
          {(s.deliverables || {}).final_delivered && <Badge color="green">Delivered</Badge>}
        </div>
      )}
    </Card>
  );
}

export default function Shoots() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("upcoming");
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const { data: shoots = [], isLoading } = useQuery({ queryKey: ["shoots"], queryFn: async () => (await api.get("/shoots")).data });
  const cmap = {}; clients.forEach((c) => (cmap[c.id] = c.name));

  const today = todayStr();
  let list = shoots;
  if (tab === "upcoming") list = shoots.filter((s) => s.date >= today && !["completed", "cancelled"].includes(s.status));
  if (tab === "completed") list = shoots.filter((s) => s.status === "completed");
  if (tab === "pending") list = shoots.filter((s) => s.status === "completed" && (!(s.outcome || {}).completed || !(s.deliverables || {}).final_delivered));

  const refresh = () => { qc.invalidateQueries({ queryKey: ["shoots"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };

  return (
    <div className="space-y-6" data-testid="shoots-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Shoots</h1>
          <p className="mt-1 text-sm text-zinc-500">{shoots.length} shoots · planning to delivery</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-shoot-button"><Plus className="mr-2 h-4 w-4" />Schedule Shoot</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="shoots-tabs">
          <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="pending">Pending Action</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {list.map((s) => <ShootCard key={s.id} s={s} clientName={cmap[s.client_id]} onClick={() => setDetail(s)} />)}
          {list.length === 0 && <div className="col-span-full py-12 text-center text-zinc-400">No shoots here</div>}
        </div>
      )}

      <ShootFormDialog open={addOpen} onOpenChange={setAddOpen} onSaved={refresh} />
      <ShootDetailDialog shoot={detail} open={!!detail} onOpenChange={(v) => !v && setDetail(null)} onSaved={refresh} />
    </div>
  );
}
